// TODO: migrate from legacy CI tooling: resolve ts-* and/or eslint-* directives below (including inline comments)
/* eslint-disable @typescript-eslint/no-floating-promises, react-hooks/exhaustive-deps, react-hooks/immutability */

import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import _ from "lodash";
import moment from "moment";

import { toast } from "react-toastify";
import { Loader, Segment } from "semantic-ui-react";

import { useAuth0 } from "../../../../auth/auth0";
import { useUserAuth } from "../../../../hooks/useUserAuth";

import * as trialActions from "../../../../redux/actions/trialActions";

import SearchPanel from "../../../Lumber/Search/SearchPanel.tsx";
import { generateBooleanFilter, generateStringFilter, getDefaultGrowingSeasons } from "../../../../utilities/search.ts";

import TrialMetaFactorsModal from "../TrialMetaTags/TrialMetaFactorsModal.tsx";
import "./styles.css";
import { TrialSearchResults } from "./TrialSearchResults.tsx";

const fieldScaleOptions = [
	{ key: "small-plot", value: "small-plot", text: "Small Plot" },
	{ key: "field-scale", value: "field-scale", text: "Field-Scale" },
];

const groundDataStatusTags = {
	"Not Created": "groundDataNotCreated",
	Planned: "groundDataPlanned",
	Collected: "groundDataCollected",
};

/**
 * Converts a trial's statuses into the tags used by the Status filter in More Filters.
 * Returns one ground data tag plus a tag for each curve status that is set.
 */
function getStatusTags(statuses) {
	if (!statuses) {
		return [];
	}

	const tags = [];
	if (groundDataStatusTags[statuses.groundDataStatus]) {
		tags.push(groundDataStatusTags[statuses.groundDataStatus]);
	}
	if (statuses.growthStatus) {
		tags.push("growthCurves");
	}
	if (statuses.senescenceStatus) {
		tags.push("senescenceCurves");
	}
	if (statuses.fullSeasonStatus) {
		tags.push("fullSeasonCurves");
	}

	return tags;
}

const TrialSearch = () => {
	const dispatch = useDispatch();
	const { getTokenSilently } = useAuth0();
	const userAuth = useUserAuth();

	// const DATE_FORMAT = "MM/DD/YYYY";

	//-- Data sources
	const clientId = useSelector((state) => (state.clients ? state.clients.currentId : null));
	const [resetDates, setResetDates] = useState(false);
	const [resetStatuses, setResetStatuses] = useState(false);

	//-- Dropdown options
	const [cropOptions, setCropOptions] = useState([]);
	const [farmOptions, setFarmOptions] = useState([]);
	const [fieldOptions, setFieldOptions] = useState([]);
	const [growingSeasonOptions, setGrowingSeasonOptions] = useState([]);
	const [trialOwnerOptions, setTrialOwnerOptions] = useState([]);
	const [companyOptions, setCompanyOptions] = useState([]);
	const [cooperatorCompanyOptions, setCooperatorCompanyOptions] = useState([]);
	const presetTimeOptions = useMemo(
		() => [
			{
				key: "noDate",
				value: "noDate",
				text: "No Plant Date",
			},
			{
				key: "thisYear",
				value: "thisYear",
				text: "This Year",
			},
			{
				key: "thisMonth",
				value: "thisMonth",
				text: "This Month",
			},
			{
				key: "thisWeek",
				value: "thisWeek",
				text: "This Week",
			},
			{
				key: "lastYear",
				value: "lastYear",
				text: "Last Year",
			},
			{
				key: "lastMonth",
				value: "lastMonth",
				text: "Last Month",
			},
			{
				key: "lastWeek",
				value: "lastWeek",
				text: "Last Week",
			},
			{
				key: "custom",
				value: "custom",
				text: "Custom",
			},
			{
				key: "allDates",
				value: "allDates",
				text: "All Dates",
			},
		],
		[resetDates],
	);
	// Recreated on reset so the MultiDropdown clears its selections
	const statusOptions = useMemo(
		() => [
			{ key: "groundDataNotCreated", value: "groundDataNotCreated", text: "Ground Data Not Created" },
			{ key: "groundDataPlanned", value: "groundDataPlanned", text: "Ground Data Planned" },
			{ key: "groundDataCollected", value: "groundDataCollected", text: "Ground Data Collected" },
			{ key: "growthCurves", value: "growthCurves", text: "Growth Curves" },
			{ key: "senescenceCurves", value: "senescenceCurves", text: "Senescence Curves" },
			{ key: "fullSeasonCurves", value: "fullSeasonCurves", text: "Full Season Curves" },
		],
		[resetStatuses],
	);
	const [locationOptions, setLocationOptions] = useState({
		cities: [],
		regions: [],
		countries: [],
	});

	//-- Selected filter options
	//-- When setting the default time filter to this year or last year, update these dates to getFullYear() (this year) or getFullYear() - 1
	//-- Also update the presetTimeId to either ("thisYear") or ("lastYear")
	//-- e.g.
	const [plantDateFrom, setPlantDateFrom] = useState(new Date(new Date().getFullYear() - 1, 0, 1));
	const [plantDateTo, setPlantDateTo] = useState(new Date(new Date().getFullYear() - 1, 11, 31));

	const [growingSeasons, setGrowingSeasons] = useState();
	const [presetTimeId, setPresetTimeId] = useState("allDates");
	const [searchAllClients, setSearchAllClients] = useState(false);
	const [trialName, setTrialName] = useState("");
	const [showNullDates, setShowNullDates] = useState(true);
	const [filters, setFilters] = useState({});

	//-- Search results
	const [trialSearchResults, setTrialSearchResults] = useState(null);
	const [trialLogData, setTrialLogData] = useState([]);

	//-- UI Control
	const [loading, setLoading] = useState(true);
	const [loadingSearchResults, setLoadingSearchResults] = useState(true);
	const [metaFactorModalOpen, setMetaFactorsModalOpen] = useState(false);
	const [modalTrialId, setModalTrialId] = useState("");
	const [modalTrialName, setModalTrialName] = useState("");

	useEffect(() => {
		//-- userAuth.isReady prevents a "Can't perform a React state update on an unmounted component..."
		if (userAuth.isReady && clientId) {
			searchForTrials(clientId);
		}
	}, [userAuth.isReady, clientId, searchAllClients]);

	useEffect(() => {
		if (trialLogData.length > 0) {
			setDropdownOptions();
		}
	}, [trialLogData]);

	useEffect(() => {
		if (trialSearchResults) {
			const logData = trialSearchResults.trialInfos.map((ti) => {
				const statuses = trialSearchResults.trialStatuses.find((ts) => ts.trialId === ti.trialId);
				return {
					...ti,
					owner: ti.trialOwnerFirstName ? `${ti.trialOwnerFirstName} ${ti.trialOwnerLastName}` : null,
					statuses,
					statusTags: getStatusTags(statuses),
					location: { city: ti.city, region: ti.region, country: ti.country, countryCode: ti.countryCode },
					sponsors: ti.sponsors,
				};
			});
			setTrialLogData(logData);
		}
	}, [trialSearchResults]);

	useEffect(() => {
		setFilters((prev) => ({
			...prev,
			plantDate: {
				type: "date",
				startDate: moment(plantDateFrom),
				endDate: moment(plantDateTo),
				showNulls: showNullDates,
				applied: presetTimeId !== "allDates" || !showNullDates,
			},
		}));
	}, [plantDateFrom, plantDateTo, showNullDates, presetTimeId]);

	const handleFilters = {
		farms: generateStringFilter("farmName", setFilters, "farmAbbr"),
		growingSeasons: generateStringFilter("growingSeasonId", setFilters, "growingSeasonName"),
		crops: generateStringFilter("cropName", setFilters),
		fields: generateStringFilter("fieldName", setFilters),
		trialOwners: generateStringFilter("owner", setFilters),
		companies: generateStringFilter("companyName", setFilters),
		cooperators: generateStringFilter("cooperatorName", setFilters),
		cities: generateStringFilter("city", setFilters),
		regions: generateStringFilter("region", setFilters),
		countries: generateStringFilter("country", setFilters),
		fieldScale: generateBooleanFilter("fieldScaleTrial", setFilters, { "field-scale": true, "small-plot": false }),
		statuses: generateStringFilter("statusTags", setFilters, "statuses"),
	};

	async function searchForTrials(clientId) {
		if (clientId && plantDateTo && plantDateFrom) {
			const accessToken = await getTokenSilently();
			setLoadingSearchResults(true);
			dispatch(
				trialActions.searchForTrials(
					{
						plantDateFrom: "01/01/1990",
						plantDateTo: "01/01/2099",
					},
					clientId,
					searchAllClients,
					accessToken,
				),
			)
				.then((res) => {
					setTrialSearchResultsState(res);
					setLoading(false);
					setLoadingSearchResults(false);
				})
				.catch((err) => {
					toast.error("Error searching for trials. Please try again.");
					setLoading(false);
					setLoadingSearchResults(false);
					console.log(err);
				});
		}
	}

	function setDropdownOptions() {
		setCropOptionsState(trialLogData);
		setFarmOptionsState(trialLogData);
		setFieldOptionsState(trialLogData);
		let currentGs = setGrowingSeasonOptionsState(trialLogData);
		setTrialOwnerOptionsState(trialLogData);
		setCompanyOptionsState(trialLogData);
		setCooperatorCompanyOptionsState(trialLogData);
		setLocationOptionsState(trialLogData);

		return currentGs;
	}

	function setCropOptionsState(crops) {
		setCropOptions(
			_.chain(crops)
				.sortBy("cropName")
				.sortedUniqBy("cropName")
				.map(({ cropId, cropName }) => {
					const opt = { key: cropId, value: cropName, text: cropName };
					return opt;
				})
				.value(),
		);
	}

	function setFarmOptionsState(farms) {
		let fOptions = _.chain(farms)
			.sortBy("farmName")
			.sortedUniqBy("farmName")
			.map(({ farmId, farmName, clientName }) => {
				const opt = {
					key: farmId,
					value: farmName,
					text: farmName,
					content: (
						<div>
							<span style={{ fontSize: 14, color: "#000000" }}>{farmName}</span>
							<span style={{ float: "right", fontSize: 12, opacity: 0.75 }}>({clientName})</span>
						</div>
					),
				};
				return opt;
			})
			.value();

		setFarmOptions(fOptions);
	}

	function setFieldOptionsState(fields) {
		let fOptions = _.chain(fields)
			.sortBy("fieldName")
			.sortedUniqBy("fieldName")
			.map(({ fieldId, fieldName, clientName }) => {
				const opt = {
					key: fieldId,
					value: fieldName,
					text: fieldName,
					content: (
						<div>
							<span style={{ fontSize: 14, color: "#000000" }}>{fieldName}</span>
							<span style={{ float: "right", fontSize: 12, opacity: 0.75 }}>({clientName})</span>
						</div>
					),
				};
				return opt;
			})
			.value();
		setFieldOptions(fOptions);
	}

	function setGrowingSeasonOptionsState(growingSeasons) {
		let tempGsOptions = _.chain(growingSeasons)
			.sortBy("growingSeasonName")
			.sortedUniqBy("growingSeasonName")
			.map(({ growingSeasonId, growingSeasonName }) => {
				const opt = { key: growingSeasonId, value: growingSeasonId, text: growingSeasonName };
				return opt;
			})
			.reverse()
			.value();
		setGrowingSeasonOptions(tempGsOptions);

		//-- Set growing season to default gs
		let defaultGrowingSeasons = getDefaultGrowingSeasons();
		let filteredSelections = _.map(
			_.filter(tempGsOptions, (gs) => defaultGrowingSeasons.includes(gs.text)),
			"value",
		);
		let defaultGrowingSeasonSelections = filteredSelections.length > 0 ? filteredSelections : [tempGsOptions[0]?.value];
		setGrowingSeasons(defaultGrowingSeasonSelections);
	}

	function setTrialOwnerOptionsState(trialOwners) {
		let tOptions = _.chain(trialOwners)
			.sortBy("owner")
			.sortedUniqBy("owner")
			.map(({ trialOwnerUserId, owner, clientName }) => {
				if (trialOwnerUserId) {
					const opt = {
						key: trialOwnerUserId,
						value: owner,
						text: owner,
						content: (
							<div>
								<span style={{ fontSize: 14, color: "#000000" }}>{owner}</span>
								<span style={{ float: "right", fontSize: 12, opacity: 0.75 }}>({clientName})</span>
							</div>
						),
					};
					return opt;
				} else {
					const opt = {
						key: "trialOwnerUser-null",
						value: null,
						text: `No Owner`,
					};
					return opt;
				}
			})
			.value();

		setTrialOwnerOptions(tOptions);
	}

	function setCompanyOptionsState(companies) {
		let cOptions = _.chain(companies)
			.sortBy("companyName")
			.sortedUniqBy("companyName")
			.map(({ companyId, companyName, clientName }) => {
				if (companyName) {
					const opt = {
						key: companyId,
						value: companyName,
						text: companyName,
						content: (
							<div>
								<span style={{ fontSize: 14, color: "#000000" }}>{companyName}</span>
								<span style={{ float: "right", fontSize: 12, opacity: 0.75 }}>({clientName})</span>
							</div>
						),
					};
					return opt;
				} else {
					const opt = {
						key: "company-null",
						value: null,
						text: "No Company",
					};
					return opt;
				}
			})
			.value();

		setCompanyOptions(cOptions);
	}

	function setCooperatorCompanyOptionsState(companies) {
		let ccOptions = _.chain(companies)
			.sortBy("cooperatorName")
			.sortedUniqBy("cooperatorName")
			.map(({ cooperatorCompanyId, cooperatorName }) => {
				const opt = {
					key: cooperatorCompanyId,
					value: cooperatorName,
					text: cooperatorName,
					content: (
						<div>
							<span style={{ fontSize: 14, color: "#000000" }}>{cooperatorName}</span>
							{/* <span style={{ float: "right", fontSize: 12, opacity: 0.75 }}>({clientName})</span> */}
						</div>
					),
				};
				return opt;
			})
			.value();

		setCooperatorCompanyOptions(ccOptions);
	}

	function setLocationOptionsState(locations) {
		const cityOpts = _.chain(locations)
			.sortBy("city")
			.sortedUniqBy("city")
			.map(({ city }) => {
				const opt = {
					key: city,
					value: city,
					text: city,
				};
				return opt;
			})
			.value();

		const regionOpts = _.chain(locations)
			.sortBy("region")
			.sortedUniqBy("region")
			.map(({ region }) => {
				const opt = {
					key: region,
					value: region,
					text: region,
				};
				return opt;
			})
			.value();

		const countryOpts = _.chain(locations)
			.sortBy("country")
			.sortedUniqBy("country")
			.map(({ country }) => {
				const opt = {
					key: country,
					value: country,
					text: country,
				};
				return opt;
			})
			.value();

		setLocationOptions({
			cities: cityOpts,
			regions: regionOpts,
			countries: countryOpts,
		});
	}

	function setTrialSearchResultsState(searchResults) {
		if (searchResults) {
			setTrialSearchResults(searchResults);
		}
	}

	const resetFilter = () => {
		//--TODO: Date and preset time need to be updated when changing back to look at current year
		setResetDates((prev) => !prev);
		setResetStatuses((prev) => !prev);
		setPlantDateFrom(new Date(2001, 0, 1));
		setPlantDateTo(new Date(2099, 0, 1));
		setPresetTimeId("allDates");
		setTrialName("");
		setDropdownOptions();
		setShowNullDates(true);
		setSearchAllClients(false);
	};

	useEffect(() => {
		if (presetTimeId) {
			if (presetTimeId === "thisYear") {
				setPlantDateFrom(new Date(new Date().getFullYear(), 0, 1));
				setPlantDateTo(new Date(new Date().getFullYear(), 11, 31));
			} else if (presetTimeId === "thisMonth") {
				let lastDayOfMonth = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).getDate();

				setPlantDateFrom(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
				setPlantDateTo(new Date(new Date().getFullYear(), new Date().getMonth(), lastDayOfMonth));
			} else if (presetTimeId === "thisWeek") {
				let currentDay = new Date();
				let diff = currentDay.getDate() - currentDay.getDay() + 1;
				let beginningOfWeek = new Date(new Date(currentDay).setDate(diff - 1));
				let endOfWeek = new Date(new Date(currentDay).setDate(diff + 5));

				setPlantDateFrom(beginningOfWeek);
				setPlantDateTo(endOfWeek);
			} else if (presetTimeId === "lastYear") {
				setPlantDateFrom(new Date(new Date().getFullYear() - 1, 0, 1));
				setPlantDateTo(new Date(new Date().getFullYear() - 1, 11, 31));
			} else if (presetTimeId === "lastMonth") {
				let lastDayOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 0).getDate();
				let year = new Date().getFullYear();
				if (new Date().getMonth() === 1) {
					year = new Date().getFullYear() - 1;
				}

				setPlantDateFrom(new Date(year, new Date().getMonth() - 1, 1));
				setPlantDateTo(new Date(year, new Date().getMonth() - 1, lastDayOfMonth));
			} else if (presetTimeId === "lastWeek") {
				let currentDay = new Date();
				let diff = currentDay.getDate() - currentDay.getDay() + 1;
				let beginningOfWeek = new Date(new Date(currentDay).setDate(diff - 8));
				let endOfWeek = new Date(new Date(currentDay).setDate(diff - 2));

				setPlantDateFrom(beginningOfWeek);
				setPlantDateTo(endOfWeek);
			} else if (presetTimeId === "allDates") {
				setPlantDateFrom(new Date(2001, 0, 1));
				setPlantDateTo(new Date(2099, 0, 1));
			} else if (presetTimeId === "noDate") {
				setPlantDateFrom(null);
				setPlantDateTo(null);
			}
		}
	}, [presetTimeId]);

	const toggleMetaFactorModal = (trialId, trialName, open = true) => {
		if (trialId) {
			setModalTrialId(trialId);
			setModalTrialName(trialName);
			setMetaFactorsModalOpen(open);
		}
	};

	return loading ? (
		<Loader active />
	) : !loading && trialLogData.length === 0 ? (
		<Segment style={{ margin: 15 }}>
			<p>{"Trial Search could not be loaded. Please try again. If the problem persists, please contact support."}</p>
		</Segment>
	) : (
		<>
			<Segment basic style={{ display: "flex", flexDirection: "column", height: "100%", padding: "0" }}>
				<SearchPanel onReset={resetFilter}>
					<SearchPanel.Bar
						searchTerm={trialName}
						setSearchTerm={setTrialName}
						placeholder="Search Trials"
						disabled={trialLogData.length === 0}
					/>
					<SearchPanel.MultiDropdown
						placeholder="Growing Seasons"
						options={growingSeasonOptions}
						defaultOptionIds={growingSeasons}
						setFilters={handleFilters.growingSeasons}
					/>
					<SearchPanel.MultiDropdown
						placeholder="Farms"
						size="small"
						options={farmOptions}
						setFilters={handleFilters.farms}
					/>
					<SearchPanel.MultiDropdown
						placeholder="Crops"
						size="small"
						options={cropOptions}
						setFilters={handleFilters.crops}
					/>
					<SearchPanel.MultiDropdown
						placeholder="Fields"
						size="small"
						options={fieldOptions}
						setFilters={handleFilters.fields}
					/>
					<SearchPanel.MultiDropdown
						searchable={false}
						options={fieldScaleOptions}
						placeholder="Trial Type"
						size="small"
						setFilters={handleFilters.fieldScale}
					/>
					<SearchPanel.AdvancedPopup
						filters={filters}
						advancedHeaders={["owner", "companyName", "cooperatorName", "plantDate", "statuses"]}
					>
						<SearchPanel.Date
							label="Plant Dates From"
							placeholder="From"
							date={plantDateFrom}
							setDate={setPlantDateFrom}
						/>
						<SearchPanel.Date label="Plant Dates To" placeholder="To" date={plantDateTo} setDate={setPlantDateTo} />
						<SearchPanel.Dropdown
							label="Preset Date Range"
							placeholder="Date Range"
							options={presetTimeOptions ?? []}
							setSelection={setPresetTimeId}
							defaultOption={presetTimeOptions[presetTimeOptions.length - 1]} // all dates
						/>
						<SearchPanel.MultiDropdown
							placeholder="Trial Owners"
							options={trialOwnerOptions}
							setFilters={handleFilters.trialOwners}
						/>
						<SearchPanel.MultiDropdown
							placeholder="Companies"
							options={companyOptions}
							setFilters={handleFilters.companies}
						/>
						<SearchPanel.MultiDropdown
							placeholder="Cooperators"
							options={cooperatorCompanyOptions}
							setFilters={handleFilters.cooperators}
						/>
						<SearchPanel.MultiDropdown
							placeholder="Cities"
							options={locationOptions.cities}
							setFilters={handleFilters.cities}
						/>
						<SearchPanel.MultiDropdown
							placeholder="States / Regions"
							options={locationOptions.regions}
							setFilters={handleFilters.regions}
						/>
						<SearchPanel.MultiDropdown
							placeholder="Countries"
							options={locationOptions.countries}
							setFilters={handleFilters.countries}
						/>
						<SearchPanel.MultiDropdown
							searchable={false}
							placeholder="Status"
							options={statusOptions}
							setFilters={handleFilters.statuses}
						/>
						<SearchPanel.Checkbox
							label="Show Results With No Plant Date"
							setChecked={setShowNullDates}
							checked={showNullDates}
						/>
						<SearchPanel.Checkbox
							label="Search All Clients"
							setChecked={setSearchAllClients}
							checked={searchAllClients}
						/>
					</SearchPanel.AdvancedPopup>
				</SearchPanel>

				<TrialSearchResults
					loading={loadingSearchResults}
					data={trialLogData}
					filters={filters}
					openMetaTagsModal={toggleMetaFactorModal}
					searchTerm={{ term: trialName, cols: ["trialName"] }}
				/>
			</Segment>
			<TrialMetaFactorsModal
				open={metaFactorModalOpen}
				setOpen={setMetaFactorsModalOpen}
				trialId={modalTrialId}
				trialName={modalTrialName}
			/>
		</>
	);
};

export default TrialSearch;
